import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { CheckSquare, Users, Clock, House, Briefcase, Coffee, ForkKnife } from "@phosphor-icons/react";

const STATUS_META = {
  IN: { label: "in_office", bg: "bg-[#00CC33]", text: "text-black" },
  OUT: { label: "absent", bg: "bg-white", text: "text-black" },
  LUNCH: { label: "lunch_start", bg: "bg-[#FFCC00]", text: "text-black" },
  COFFEE: { label: "coffee", bg: "bg-[#FFCC00]", text: "text-black" },
  CIGARETTE: { label: "cigarette", bg: "bg-[#FFCC00]", text: "text-black" },
  BUSINESS_TRIP: { label: "business_trip", bg: "bg-[#FF8000]", text: "text-black" },
  WFH: { label: "wfh", bg: "bg-[#FF66B2]", text: "text-black" },
  ANNUAL: { label: "annual", bg: "bg-[#0047FF]", text: "text-white" },
  SICK: { label: "sick", bg: "bg-[#FF3333]", text: "text-white" },
  UNPAID: { label: "unpaid", bg: "bg-black", text: "text-white" },
};

function formatTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  } catch {
    return "—";
  }
}

export default function Dashboard() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [presence, setPresence] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/presence/live");
      setPresence(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 10000);
    return () => clearInterval(id);
  }, []);

  const counts = presence.reduce(
    (acc, p) => {
      acc.total += 1;
      if (p.status === "IN") acc.in += 1;
      else if (["LUNCH", "COFFEE", "CIGARETTE"].includes(p.status)) acc.breaks += 1;
      else if (p.status === "WFH") acc.wfh += 1;
      else if (["ANNUAL", "SICK", "UNPAID"].includes(p.status)) acc.leave += 1;
      else acc.out += 1;
      return acc;
    },
    { total: 0, in: 0, breaks: 0, wfh: 0, leave: 0, out: 0 }
  );

  return (
    <div>
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-black/60">{t("dashboard")}</div>
          <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter leading-none">
            {t("live_presence")}
          </h1>
        </div>
        <div className="text-xs font-mono text-black/60">Auto-refresh 10s</div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <SummaryCard icon={Users} label={t("total_employees")} value={counts.total} testid="stat-total" />
        <SummaryCard icon={Clock} label={t("in_office")} value={counts.in} bg="bg-[#00CC33]" testid="stat-in" />
        <SummaryCard icon={Coffee} label={t("on_break")} value={counts.breaks} bg="bg-[#FFCC00]" testid="stat-break" />
        <SummaryCard icon={House} label={t("wfh")} value={counts.wfh} bg="bg-[#FF66B2]" testid="stat-wfh" />
        <SummaryCard icon={Briefcase} label={t("on_leave")} value={counts.leave} bg="bg-[#0047FF]" invert testid="stat-leave" />
      </div>

      {/* Table */}
      <div className="mt-6 brut-border bg-white brut-shadow overflow-hidden">
        <div className="border-b-2 border-black p-4 flex items-center justify-between">
          <div className="font-display font-black text-xl tracking-tighter">{t("employees")}</div>
          <div className="text-xs font-mono text-black/60">{loading ? "…" : `${presence.length}`}</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F4F4F0] uppercase tracking-wider text-xs">
              <tr>
                <th className="text-left p-3 border-b-2 border-black">{t("name")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("team")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("status")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("last_event")}</th>
                <th className="text-left p-3 border-b-2 border-black">{t("time")}</th>
              </tr>
            </thead>
            <tbody data-testid="presence-table">
              {presence.map((p) => {
                const meta = STATUS_META[p.status] || STATUS_META.OUT;
                return (
                  <tr key={p.user.id} className="border-b border-black/10" data-testid={`presence-row-${p.user.id}`}>
                    <td className="p-3 font-bold">{p.user.name}<div className="text-xs font-mono text-black/60">{p.user.email}</div></td>
                    <td className="p-3">{p.user.team || "—"}</td>
                    <td className="p-3">
                      <span className={`tag ${meta.bg} ${meta.text}`}>{t(meta.label)}</span>
                    </td>
                    <td className="p-3 font-mono text-xs">{p.last_event?.event_type || "—"}</td>
                    <td className="p-3 font-mono text-xs">{formatTime(p.last_event?.timestamp)}</td>
                  </tr>
                );
              })}
              {!loading && presence.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-black/60">{t("no_data")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ icon: Icon, label, value, bg = "bg-white", invert = false, testid }) {
  return (
    <div className={`brut-border ${bg} ${invert ? "text-white" : "text-black"} p-4 brut-shadow`} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Icon size={24} weight="bold" />
        <div className="font-display font-black text-4xl leading-none tracking-tighter">{value}</div>
      </div>
      <div className="mt-2 text-[10px] font-bold uppercase tracking-[0.15em] opacity-80">{label}</div>
    </div>
  );
}
